import a from "indefinite";
import { compact } from "lodash";
import { useContext, useState } from "react";
import {
  CompetencyNode,
  CreatedArtifactFragmentFragment,
  GoalState,
} from "types/graphql-schema";
import { GoalScope } from "types/graphql-schema";
import { ArtifactType } from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import ArtifactCreationDialog from "@apps/artifact-creation-dialog/artifact-creation-dialog";
import Artifact from "@apps/artifact/artifact";
import getDevelopmentGoalsRelatedToCompetenciesQuery from "@apps/assessments/graphql/get-development-goals-related-to-competencies-query";
import getDashboardGoalsQuery from "@apps/dashboard-new/graphql/get-dashboard-goals-query";
import getCareerRoleRelatedDataQuery from "@apps/profile/career-tab/graphql/get-career-role-related-data-query";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Button, { buttonTheme } from "@components/button/button";
import { ButtonSize } from "@components/button/button";
import GoalIcon from "@components/goal-icon/goal-icon";
import AppPopover from "@components/popover/app-popover";
import {
  TopicflowIcon,
  TopicflowIconMap,
} from "@components/topicflow-icon/topicflow-icon";
import { getWatchedQueries } from "@graphql/client";
import { assertEdgesNonNull } from "@helpers/helpers";

import { AssessmentQuestionRole } from "../questions/types";
import { RoleBasedAssessmentDevelopmentGoalsContext } from "./role-based-assessment-delivery-question-group";

const RoleBasedAssessmentGoalPopover = ({
  role,
  competency,
  target,
}: {
  role: AssessmentQuestionRole;
  competency: Pick<CompetencyNode, "id" | "title">;
  target?: BasicUser | null;
}) => {
  const label = useLabel();
  const [showAddGoalModal, setShowAddGoalModal] = useState(false);

  const { goals, isOnDeliveryPage } = useContext(
    RoleBasedAssessmentDevelopmentGoalsContext
  );

  const organization = currentOrganizationVar();
  if (!organization.featureFlags.goals) {
    return null;
  }

  // filter by matching competency
  const filteredGoals = goals.filter((goal) => {
    const relations = goal.competencyRelations
      ? assertEdgesNonNull(goal.competencyRelations)
      : [];
    return relations.some(
      (relation) =>
        relation.competencyId === competency.id && relation.roleId === role.id
    );
  });

  const whyAreWeHiding = !isOnDeliveryPage
    ? "will not show on the assessment page, only delivery page is supported."
    : null;

  if (whyAreWeHiding) {
    return null;
  }

  const tooltipText =
    filteredGoals.length === 0
      ? `Add ${a(label("development"))} ${label("goal")}`
      : `${label("development", {
          capitalize: true,
        })} ${label("goal", {
          pluralize: true,
        })} connected to this ${label("competency")}.`;

  return (
    <div className="flex gap-2 items-center">
      {showAddGoalModal && (
        <ArtifactCreationDialog
          hideTypeSelector
          openSidebarAfterCreation
          formOptions={{
            artifactType: ArtifactType.Goal,
            scope: GoalScope.Development,
            owners: compact([target]),
            competencyIds: [
              {
                competencyId: competency.id,
                roleId: role.id,
              },
            ],
          }}
          customFields={[
            {
              label: label("competency", { titleCase: true }),
              renderContent: (form) =>
                form.scope === GoalScope.Development ? (
                  <div className="flex">
                    <div className="py-0.5 px-1.5 bg-gray-100 rounded">
                      {competency.title} ({role.title})
                    </div>
                  </div>
                ) : null,
            },
          ]}
          onClose={(createdArtifact?: CreatedArtifactFragmentFragment) => {
            setShowAddGoalModal(false);

            // Update the goals list
            if (createdArtifact) {
              const queries = getWatchedQueries(
                getDevelopmentGoalsRelatedToCompetenciesQuery
              );
              const matchingQuery = queries.find((query) => {
                return (
                  query.options.variables?.roleId === role.id &&
                  (query.options.variables?.competencyIds || []).includes(
                    competency.id
                  )
                );
              });
              if (matchingQuery) {
                matchingQuery.refetch();
              }
            }
          }}
          refetchQueries={[
            getCareerRoleRelatedDataQuery,
            getDevelopmentGoalsRelatedToCompetenciesQuery,
            getDashboardGoalsQuery,
          ]}
        />
      )}
      {filteredGoals.length === 0 ? (
        <Button
          size={ButtonSize.medium}
          onClick={() => setShowAddGoalModal(true)}
          tooltip={tooltipText}
          icon={TopicflowIconMap.plus}
        />
      ) : (
        <AppPopover
          content={
            <div className="flex flex-col gap-3 px-4 py-3 text-sm w-96">
              {filteredGoals.map((goal) => (
                <div key={goal.id} className="truncate">
                  <Artifact
                    artifact={goal}
                    size={5}
                    hideActionDropdown
                    showTitleOnlyOnOneLine
                  />
                </div>
              ))}

              <div className="-mx-3">
                <Button
                  theme={buttonTheme.text}
                  onClick={() => setShowAddGoalModal(true)}
                  fullWidth
                >
                  <TopicflowIcon type="plus" className="h-5 w-5" />
                  Create {label("development", { titleCase: true })}{" "}
                  {label("goal", { titleCase: true })}
                </Button>
              </div>
            </div>
          }
        >
          <AppPopover.Button
            as={Button}
            size={ButtonSize.medium}
            tooltip={tooltipText}
          >
            <GoalIcon
              size="4"
              state={GoalState.Open}
              scope={GoalScope.Development}
              hideTooltip
            />
            {filteredGoals.length}
          </AppPopover.Button>
        </AppPopover>
      )}
    </div>
  );
};

export default RoleBasedAssessmentGoalPopover;
